import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  runSearch,
  type SearchAIProvider,
  type SearchMode,
  type SearchResult,
} from '../lib/api';
import { colorHexFor } from '../lib/colors';

/**
 * SearchPanel — D.6 search UI polish (ARCH §8.4).
 *
 * Two surfaces, one component:
 *   - Home → no projectId → cross-project search
 *   - ProjectPage → projectId set → scoped to that project
 *
 * Two modes:
 *   - Match (default) — flat ranked list grouped by source (notes / tasks /
 *     permits / etc.)
 *   - AI — synthesized paragraph at the top + the underlying matches below
 *     so the user can verify the synthesis against the raw hits
 *
 * Results follow the standard left-edge-stripe visual language: each result
 * card carries the project's color stripe on the left so multi-project
 * search feels visually consistent with Home cards + alert rows.
 */
export function SearchPanel({
  projectId,
  placeholder,
}: {
  projectId?: string;
  placeholder?: string;
}) {
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [mode, setMode] = useState<SearchMode>('match');
  // Default to Mock so AI mode always returns something even without a
  // local Qwen running. User can switch to Local Qwen / Claude when those
  // providers are configured + reachable. Mirrors the same dropdown
  // pattern as the Client Updates compose modal (D-036).
  const [aiProvider, setAiProvider] = useState<SearchAIProvider>('mock');

  const searchQ = useQuery({
    queryKey: [
      'search',
      submitted,
      mode,
      projectId ?? 'all',
      mode === 'ai' ? aiProvider : '',
    ],
    queryFn: () =>
      runSearch(submitted, {
        scope: projectId ? 'project' : 'all',
        project_id: projectId,
        mode,
        provider: mode === 'ai' ? aiProvider : undefined,
        limit: 30,
      }),
    enabled: submitted.length > 0,
  });

  const grouped = useMemo(
    () => groupBySource(searchQ.data?.results ?? []),
    [searchQ.data]
  );

  return (
    <section>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(query.trim());
        }}
        className="flex items-center gap-2"
      >
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={
            placeholder ??
            (projectId
              ? 'Search this project (notes, tasks, permits, plans, pay apps)…'
              : 'Search across all projects (notes, tasks, permits, plans, pay apps)…')
          }
          className="flex-1 border border-border rounded-md bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <ModeToggle mode={mode} onChange={setMode} />
        {mode === 'ai' && (
          <select
            value={aiProvider}
            onChange={(e) => setAiProvider(e.target.value as SearchAIProvider)}
            className="border border-border rounded-md bg-surface px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent"
            title="AI provider for synthesis"
          >
            <option value="mock">Mock</option>
            <option value="local">Local Qwen</option>
            <option value="cloud">Claude</option>
          </select>
        )}
        <button
          type="submit"
          disabled={!query.trim()}
          className="px-3 py-2 rounded-md text-sm font-medium text-surface focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50"
          style={{ background: 'var(--color-accent)' }}
        >
          Search
        </button>
        {submitted && (
          <button
            type="button"
            onClick={() => {
              setQuery('');
              setSubmitted('');
            }}
            className="text-xs text-ink-3 hover:text-ink underline"
          >
            clear
          </button>
        )}
      </form>

      {submitted && (
        <div className="mt-3 space-y-3">
          {searchQ.isLoading && (
            <p className="text-sm text-ink-3 italic">Searching…</p>
          )}
          {searchQ.isError && (
            <p
              className="text-sm"
              style={{ color: 'var(--color-danger)' }}
            >
              {(searchQ.error as Error).message}
            </p>
          )}
          {searchQ.data && searchQ.data.count === 0 && (
            <p className="text-sm text-ink-3 italic">
              No results for &ldquo;{submitted}&rdquo;.
            </p>
          )}

          {/* AI synthesis — when in AI mode + results exist */}
          {searchQ.data?.ai?.text && (
            <div
              className="border border-border rounded-md p-3"
              style={{
                background: 'var(--color-accent-soft)',
                borderColor: 'var(--color-accent)',
              }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide mb-1.5"
                 style={{ color: 'var(--color-accent)' }}>
                AI · {searchQ.data.ai.model}
              </p>
              <p className="text-sm text-ink whitespace-pre-wrap leading-relaxed">
                {searchQ.data.ai.text}
              </p>
            </div>
          )}

          {/* Grouped match results */}
          {searchQ.data && searchQ.data.count > 0 && (
            <div className="space-y-3">
              {grouped.map(([sourceLabel, items]) => (
                <SourceGroup
                  key={sourceLabel}
                  sourceLabel={sourceLabel}
                  items={items}
                  showProject={!projectId}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// ModeToggle — match vs AI
// ---------------------------------------------------------------------------

function ModeToggle({
  mode,
  onChange,
}: {
  mode: SearchMode;
  onChange: (m: SearchMode) => void;
}) {
  return (
    <div
      className="inline-flex rounded-md border border-border overflow-hidden text-xs"
      role="radiogroup"
      aria-label="Search mode"
    >
      <button
        type="button"
        role="radio"
        aria-checked={mode === 'match'}
        onClick={() => onChange('match')}
        className={`px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-accent ${
          mode === 'match'
            ? 'bg-ink text-surface font-medium'
            : 'bg-surface text-ink-2 hover:text-ink hover:bg-surface-2'
        }`}
        title="Match: ranked list of FTS5 snippets"
      >
        Match
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={mode === 'ai'}
        onClick={() => onChange('ai')}
        className={`px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-accent ${
          mode === 'ai'
            ? 'bg-ink text-surface font-medium'
            : 'bg-surface text-ink-2 hover:text-ink hover:bg-surface-2'
        }`}
        title="AI: same matches + a synthesized paragraph from the top results"
      >
        AI
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SourceGroup — header + result rows for a single source type
// ---------------------------------------------------------------------------

function SourceGroup({
  sourceLabel,
  items,
  showProject,
}: {
  sourceLabel: string;
  items: SearchResult[];
  showProject: boolean;
}) {
  return (
    <div>
      <h3 className="text-xs font-semibold tracking-wide uppercase text-ink-2 mb-1.5 border-b border-border pb-0.5">
        {prettySource(sourceLabel)}{' '}
        <span className="text-ink-3 font-normal num">({items.length})</span>
      </h3>
      <ul className="space-y-1.5">
        {items.map((r) => (
          <ResultRow
            key={`${r.source}-${r.id}`}
            result={r}
            showProject={showProject}
          />
        ))}
      </ul>
    </div>
  );
}

function ResultRow({
  result,
  showProject,
}: {
  result: SearchResult;
  showProject: boolean;
}) {
  const projectColor = result.project_color
    ? colorHexFor(result.project_color)
    : 'var(--color-border)';
  const href = hrefFor(result);

  return (
    <li>
      <Link
        to={href}
        className="block border border-border rounded-md hover:border-border-strong focus:outline-none focus:ring-2 focus:ring-accent overflow-hidden"
      >
        <div className="flex items-stretch bg-surface">
          {/* Left-edge project color stripe — same vocabulary as Home cards
              + alert rows: stripe = "which project does this belong to" */}
          <div
            aria-hidden
            style={{ background: projectColor, width: 4 }}
            className="flex-shrink-0"
            title={`Project: ${result.project_name ?? '?'}`}
          />
          <div className="flex-1 px-3 py-2 min-w-0">
            <div className="flex items-center gap-2 mb-0.5">
              {showProject && result.project_name && (
                <>
                  <span className="text-xs num text-ink-2 font-semibold">
                    {result.project_name}
                  </span>
                  <span aria-hidden className="text-ink-3 text-xs">·</span>
                </>
              )}
              <span className="text-sm font-medium text-ink truncate">
                {result.title}
              </span>
            </div>
            {result.snippet && (
              <p
                className="text-xs text-ink-2 leading-snug snippet-mark"
                // FTS5 snippets contain <mark>...</mark> highlight tags
                dangerouslySetInnerHTML={{ __html: result.snippet }}
              />
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Helpers — pretty-source, href derivation, grouping
// ---------------------------------------------------------------------------

const SOURCE_DISPLAY_ORDER = [
  'note',
  'activity_log',
  'task',
  'permit',
  'tracked_plan',
  'tracked_document',
  'pay_app',
];

function groupBySource(
  results: SearchResult[]
): Array<[string, SearchResult[]]> {
  const map = new Map<string, SearchResult[]>();
  for (const r of results) {
    if (!map.has(r.source)) map.set(r.source, []);
    map.get(r.source)!.push(r);
  }
  // Sort keys by display order, with anything unknown trailing
  const ordered = [...map.entries()].sort(([a], [b]) => {
    const ai = SOURCE_DISPLAY_ORDER.indexOf(a);
    const bi = SOURCE_DISPLAY_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });
  return ordered;
}

function prettySource(s: string): string {
  switch (s) {
    case 'note': return 'Notes';
    case 'activity_log': return 'Client Updates';
    case 'task': return 'Tasks';
    case 'permit': return 'Permits';
    case 'tracked_plan': return 'Plans';
    case 'tracked_document': return 'Documents';
    case 'pay_app': return 'Pay Apps';
    default: return s;
  }
}

/** Best-effort deep link per source. Build E will tighten these to land on
 *  the specific row (e.g., open a permit modal directly). */
function hrefFor(r: SearchResult): string {
  switch (r.source) {
    case 'note':
      return `/projects/${r.project_id}/notes`;
    case 'activity_log':
      return `/projects/${r.project_id}/notes?tab=client`;
    case 'task':
      return `/projects/${r.project_id}`;
    case 'permit':
      return `/projects/${r.project_id}/permits`;
    case 'tracked_plan':
      return `/projects/${r.project_id}/plans-tracker`;
    case 'tracked_document':
      return `/projects/${r.project_id}/documents-tracker`;
    case 'pay_app':
      return `/projects/${r.project_id}/pay-apps`;
    default:
      return `/projects/${r.project_id}`;
  }
}
